<template>
  <BlockContainer :tight="true">
    <BlockFilter class="mx-auto" />

    <!-- Stack -->
    <template v-for="({ type, chunk }, i) in stack">
      <BlockTileStack
        v-if="type === 'brand'"
        :key="i"
        :stack="chunk"
        :reposition="reposition"
        class="transition-opacity"
        :class="{ 'opacity-30 pointer-events-none': searching }"
        @positionItems="stackInit = true"
      >
        <template v-slot:default="slotProps">
          <DirectoryBrandTile
            v-for="({ brandName, thumbnailImage, url }, i) in chunk"
            :key="i"
            @load="slotProps.handleLoad()"
            class="p-grid"
            :class="tileClass"
            :title="brandName"
            :image="thumbnailImage"
            :link="url"
          />
        </template>
      </BlockTileStack>

      <DirectoryStoryTile
        v-if="type === 'story'"
        :key="i"
        :slug="chunk.slug"
        :title="chunk.title"
        :color="chunk.cardColour"
        :image="chunk.directoryCardThumbnailImage[0]"
      />
    </template>

    <BlockLazyLoadMore
      class="mt-16 lg:mt-24"
      @loadMore="handleLoadMore"
      :end="end"
      v-if="stackInit && !Object.values(active).length"
    />

    <!-- Messaging -->
    <div
      class="flex-grow flex items-center justify-center transition-opacity pointer-events-none"
      :class="{
        'opacity-0 h-0': !noResults,
        'opacity-30': searching
      }"
    >
      <p CLASS="font-title uppercase Leading-none text-2-mob lg:text-3-desk">
        NO RESULTS FOUND :-(
      </p>
    </div>

    <!-- Sticky Buttons -->
    <template v-slot:buttonArea>
      <div
        @click="largeTiles = !largeTiles"
        class="mb-8 transition-opacity hidden lg:block cursor-pointer"
        :class="{
          'opacity-0 pointer-events-none': !(stack && stack.length)
        }"
      >
        <CoreIcon v-if="!largeTiles" :width="32" color="brown">
          <IconPlus />
        </CoreIcon>
        <CoreIcon v-if="largeTiles" :width="32" color="brown">
          <IconMinus />
        </CoreIcon>
      </div>
    </template>
  </BlockContainer>
</template>

<script>
import { mapState } from "vuex";
import { getLatest as getLatestBrands, getActive } from "@io/directory";
import { handleLoadMore } from "@io/helpers";
import { getLatest as getLatestStories } from "@io/story";
import { isEmptyObject } from "@utils/types";
import {
  BlockContainer,
  BlockFilter,
  BlockTileStack,
  BlockLazyLoadMore
} from "@c/Block";
import { IconPlus, IconMinus } from "@c/Icon";
import DirectoryBrandTile from "./DirectoryBrandTile";
import DirectoryStoryTile from "./DirectoryStoryTile";

export default {
  name: "Directory",

  meta: {
    title: ""
  },

  components: {
    BlockContainer,
    BlockFilter,
    BlockTileStack,
    BlockLazyLoadMore,
    DirectoryBrandTile,
    DirectoryStoryTile,
    IconPlus,
    IconMinus
  },

  data: () => ({
    brands: null,
    stories: null,
    searchResults: [],
    largeTiles: false,
    loading: false /* Loading more brands - internal */,
    searching: false /* Peforming new search */,
    reposition: 0,
    end: false,
    stackInit: false
  }),

  computed: {
    ...mapState({
      active: state => state.directory.active,
      isMobile: state => state.ui.isMobile
    }),
    stack() {
      if (this.noResults) return [];

      // Checking against searchResults not filters as this allows the feed to
      // persist during a search, protections are built into the search function
      const brands = this.searchResults.length
        ? this.searchResults
        : this.brands;

      if (!brands) return brands;

      const cols = 6;
      const rows = 6;
      const chunkSize = cols * rows;

      const chunks = [];
      const j = brands.length; // Cache value for micro optimisation
      const l = this.stories ? this.stories.length : 0;
      let k = 0;

      for (let i = 0; i < j; i += chunkSize) {
        // If no more stories append the remaining to the last chunk
        if (k === l) {
          chunks.push({
            type: "brand",
            chunk: brands.slice(i)
          });

          break;
        }

        // Push chunk
        chunks.push({
          type: "brand",
          chunk: brands.slice(i, i + chunkSize)
        });

        // Inset a story after every chunk
        if (k < l) {
          chunks.push({
            type: "story",
            chunk: this.stories[k]
          });

          k += 1;
        }
      }

      return chunks;
    },
    tileClass() {
      if (this.isMobile) return "w-1/2";
      return this.largeTiles ? "w-1/3" : "w-1/6";
    },
    noResults() {
      if (this.searching) return false;
      return this.searchResults.length === 0 && !isEmptyObject(this.active);
    }
  },

  watch: {
    async active(val) {
      // If no search search base state
      if (isEmptyObject(val)) {
        this.searchResults = [];
        this.searching = false;
        this.end = false;
        return;
      }

      this.searching = true;
      const searchResults = await getActive();

      // This protects against if a request resolves after a user has cleared
      // filters
      if (isEmptyObject(this.active)) {
        this.searchResults = [];
        return;
      }

      // Only update the results if the id is the latest
      if (searchResults !== null) {
        this.searchResults = searchResults;
        this.searching = false;
        this.end = false;
      }
    },
    tileClass() {
      this.reposition += 1;
    }
  },

  async created() {
    const brands = await getLatestBrands({ limit: 36 });
    this.brands = Object.values(brands);

    const stories = await getLatestStories({ limit: 3 });
    this.stories = Object.values(stories);
  },

  methods: {
    async handleLoadMore() {
      // The 36 brand limit that matches the number of brands before a story
      // displays maens that brnadsdont appear before the story whrn loading

      if (Object.values(this.active).length === 0) {
        handleLoadMore.call(this, getLatestBrands, 36, "brands");
      }
    }
  }
};
</script>

<style scoped>
.p-grid {
  padding: 0.625rem;
}
</style>
